import styled from 'styled-components';

import { Flex, FlexProps } from '@electricjs/arc';

import noImagePicture from '@/assets/products/no-image.png';

const ImageSize = {
  regularCardSize: 'regularCardSize',
  condensedCardSize: 'condensedCardSize',
  cartSize: 'cartSize',
} as const;

type ImageSizeType = (typeof ImageSize)[keyof typeof ImageSize];

/**
 * Return the dimensions (width and height) for each size, keeping the same ratio (4:3).
 * width/height = 4/3 = 1.3333
 */
const getDimensionsBySize = (size: ImageSizeType) => {
  switch (size) {
    case ImageSize.regularCardSize:
      return {
        width: '25rem', // Ratio: 25/18.8 = 1.3297. Almost 4:3 (1.3333).
        height: '18.8rem',
      };
    case ImageSize.condensedCardSize:
      return {
        width: '13.3rem', // Ratio: 13.3/10 = 1.33. Almost 4:3 (1.3333).
        height: '10rem',
      };
    case ImageSize.cartSize:
      return {
        width: '9.2rem', // Ratio: 9.2/6.9 = 1.3333. Exactly 4:3 (1.3333).
        height: '6.9rem',
      };
  }
};

const ImageArea = styled(Flex)<{ $size: ImageSizeType }>`
  width: ${({ $size }) => getDimensionsBySize($size)?.width};
  height: ${({ $size }) => getDimensionsBySize($size)?.height};
  justify-content: center;
  align-items: center;

  /*
    Forcing "white" background while the uploaded images don't come with transparent background.
    After that, this background should have the same color of the card's background (to work with light/dark modes, even on hover).
  */
  background-color: white; // getToken(ColorsV2.BACKGROUND_CONTENT)
`;

const StyledImage = styled.img`
  height: 100%; /* Must have the same height of the ImageArea */
  max-width: 100%; /* As the height was set, the width will naturally fit, but let's set a max-width just in case */
`;

type ItemImageProps = Pick<
  FlexProps,
  'flex' | 'flexGrow' | 'flexShrink' | 'flexBasis'
> & {
  size: ImageSizeType;
  src?: string;
  alt?: string;
};

export const ItemImage = ({ size, src, alt, ...flexProps }: ItemImageProps) => {
  return (
    <ImageArea $size={size} {...flexProps}>
      <StyledImage
        src={src ?? noImagePicture}
        alt={alt ?? 'No image or description'}
      />
    </ImageArea>
  );
};
